import { useAuth0 } from '@auth0/auth0-react'
import useSWR from 'swr'
import { fetchLanguages, LanguageAuthError, type Language } from '@/lib/bff/languages'

/*
 * Loads the languages available on a tenant from the BFF (the source of truth
 * for tenant language availability). The Auth0 access token is fetched on
 * demand and forwarded with the active tenant id. The request only runs once
 * there is an authenticated session AND an active tenant, and is keyed by
 * tenant so switching tenant refetches the correct list. Retries are manual so
 * a failed resolution surfaces an error state instead of hammering the endpoint.
 */
export function useLanguages(tenantId: string | null) {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0()

  const { data, error, isLoading, mutate } = useSWR<Language[]>(
    isAuthenticated && tenantId ? ['bff/languages', tenantId] : null,
    async ([, id]: [string, string]) => {
      const token = await getAccessTokenSilently()
      if (!token) throw new LanguageAuthError('No access token available')
      return fetchLanguages(token, id)
    },
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  )

  return {
    languages: data,
    error,
    isLoading,
    retry: () => mutate(),
  }
}
